import type { LoadResult, ScriptSource } from "./script-source.js";

/**
 * Which source a string is for.
 *
 * The contract carries one `loadScript` taking one string, and from web ticket
 * 09 there are two kinds of thing a user can put in it: a link to a document,
 * or the script itself. Something has to choose, and the choice is made from
 * the input rather than from a mode the user set, for a reason that is not
 * about saving them a click — **what is remembered across a reload is the
 * string and nothing else** (FR-18). A mode would have to be remembered
 * alongside it, on a channel that does not exist, and a stored script would
 * come back through the wrong source the first time the two disagreed.
 *
 * It is deliberately a *link* test rather than a *Google Doc* test. A Sheets
 * link, or any other URL, is still a link, and it belongs at the source that
 * will tell the user "that's not a Google Doc" — routing it to the paste source
 * instead would silently load somebody's spreadsheet URL as a one-line script.
 */
export function looksLikeALink(input: string): boolean {
  const trimmed = input.trim();
  // A script has spaces in it; a URL does not. This is the whole of what keeps
  // a pasted paragraph out of the link source, and it is checked first because
  // it is the cheapest and the most decisive.
  if (trimmed.length === 0 || /\s/u.test(trimmed)) return false;
  if (/^https?:\/\//iu.test(trimmed)) return true;
  // A bare host, which is how a browser's address bar shows a copied link — but
  // only with a path after it. Without that rule `file.txt` is a hostname, and
  // so is any one-word script with a full stop at the end.
  return /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}\//iu.test(trimmed);
}

/**
 * The two script sources behind one call.
 *
 * It is a `ScriptSource` itself, so nothing downstream can tell there are two:
 * the controller retains what it is given, the word index is built from it and
 * the prompter draws it the same either way. Refresh works for the same reason
 * — it re-loads the remembered string, which routes back to wherever it came
 * from.
 */
export class RoutedScriptSource implements ScriptSource {
  readonly #link: ScriptSource;
  readonly #pasted: ScriptSource;

  constructor(link: ScriptSource, pasted: ScriptSource) {
    this.#link = link;
    this.#pasted = pasted;
  }

  load(input: string): Promise<LoadResult> {
    // The link is trimmed and the script is not, which is the one asymmetry
    // here and is the right way round: surrounding whitespace on a copied URL
    // is an artefact of copying it, and surrounding whitespace in a script is
    // the writer's.
    return looksLikeALink(input)
      ? this.#link.load(input.trim())
      : this.#pasted.load(input);
  }
}
